"use client";

import { Add2, Remove } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { startTransition, useOptimistic, useState } from "react";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { groupSizeOptions, type SizeCategory } from "@/lib/utils/sizes";
import type { PLPFilter, PLPFilterOption } from "../types";

interface FiltersProps {
	filters: PLPFilter[];
	selectedByKey: Record<string, string[]>;
	/**
	 * Per-group set of option values that the current response still has
	 * positive counts for. Anything outside this set (and not currently
	 * selected) is rendered as disabled.
	 */
	availableByKey: Record<string, Set<string>>;
	toggleFilter: (key: string, value: string) => void;
	isLoading?: boolean;
}

const SKELETON_GROUP_COUNT = 7;

export const Filters = ({
	filters,
	selectedByKey,
	availableByKey,
	toggleFilter,
	isLoading = false,
}: FiltersProps) => {
	const t = useTranslations();

	// Auto-open at first mount: the first group is always expanded, plus any
	// group that already has a selection so the active state stays visible.
	// Once mounted, open state is fully user-controlled.
	const [openKeys, setOpenKeys] = useState<Set<string>>(() => {
		const keys = new Set<string>();
		const first = filters[0]?.key;
		if (first) keys.add(first);
		for (const [key, vals] of Object.entries(selectedByKey)) {
			if (vals.length > 0) keys.add(key);
		}
		return keys;
	});

	const toggleOpen = (key: string) => {
		setOpenKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	if (isLoading) {
		return (
			<div className="flex flex-col" aria-busy="true">
				{Array.from({ length: SKELETON_GROUP_COUNT }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list, indices are stable
					<FilterGroupSkeleton key={i} />
				))}
			</div>
		);
	}

	if (filters.length === 0) {
		return (
			<Typography variant="body-sm-reg" className="text-text-dark-text-2">
				{t("No filters available")}
			</Typography>
		);
	}

	return (
		<div className="flex flex-col">
			{filters.map((group) => (
				<FilterGroup
					key={group.key}
					group={group}
					selectedValues={selectedByKey[group.key] ?? []}
					availableSet={availableByKey[group.key]}
					isOpen={openKeys.has(group.key)}
					onToggleOpen={() => toggleOpen(group.key)}
					toggleFilter={toggleFilter}
				/>
			))}
		</div>
	);
};

const FilterGroup = ({
	group,
	selectedValues,
	availableSet,
	isOpen,
	onToggleOpen,
	toggleFilter,
}: {
	group: PLPFilter;
	selectedValues: string[];
	availableSet: Set<string> | undefined;
	isOpen: boolean;
	onToggleOpen: () => void;
	toggleFilter: (key: string, value: string) => void;
}) => {
	const selectedSet = new Set(selectedValues);
	const Icon = isOpen ? Remove : Add2;
	const activeCount = selectedValues.length;
	const triggerId = `filter-trigger-${group.key}`;
	const panelId = `filter-panel-${group.key}`;
	const hasColorSwatches = group.options.some((o) => o.hex);
	// Color pills hug a little tighter (4px) than text-only pills (6px) per Figma.
	const pillGap = hasColorSwatches
		? "gap-grid-component-spacing-4xs"
		: "gap-grid-component-spacing-3xs";

	return (
		<div className="w-full border-b border-border-medium">
			<button
				type="button"
				id={triggerId}
				onClick={onToggleOpen}
				aria-expanded={isOpen}
				aria-controls={panelId}
				className="flex w-full cursor-pointer items-center gap-grid-component-spacing-4xs py-grid-component-spacing-sm text-left"
			>
				<span className="flex flex-1 min-w-0 items-center gap-grid-component-spacing-4xs">
					<Typography as="span" variant="label-sm-reg" className="min-w-0 text-text-sand-text-4">
						{group.label}
					</Typography>
					{activeCount > 0 && (
						<span
							aria-hidden
							className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-background-bg-dark text-text-light-text-1 text-2xs leading-none tabular-nums"
						>
							{activeCount}
						</span>
					)}
				</span>
				<span
					aria-hidden
					className="inline-flex size-6 shrink-0 items-center justify-center rounded-radius-component-specific-button"
				>
					<Icon className="size-3.5 text-text-dark-text-1" />
				</span>
			</button>
			<section
				id={panelId}
				aria-labelledby={triggerId}
				aria-hidden={!isOpen}
				className={clsx(
					"grid overflow-hidden transition-[grid-template-rows] duration-300",
					isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="min-h-0 overflow-hidden">
					<div className="py-grid-component-spacing-md">
						{group.type === "toggle" ? (
							<SizeToggleGrid
								group={group}
								selectedSet={selectedSet}
								availableSet={availableSet}
								toggleFilter={toggleFilter}
							/>
						) : (
							<FilterPillList
								group={group}
								selectedSet={selectedSet}
								availableSet={availableSet}
								toggleFilter={toggleFilter}
								gapClass={pillGap}
							/>
						)}
					</div>
				</div>
			</section>
		</div>
	);
};

const FilterPillList = ({
	group,
	selectedSet,
	availableSet,
	toggleFilter,
	gapClass,
}: {
	group: PLPFilter;
	selectedSet: Set<string>;
	availableSet: Set<string> | undefined;
	toggleFilter: (key: string, value: string) => void;
	gapClass: string;
}) => {
	return (
		<div className={clsx("flex flex-wrap content-start items-start", gapClass)}>
			{group.options.map((option) => (
				<FilterPill
					key={`${group.key}:${option.value}`}
					option={option}
					isSelectedFromUrl={selectedSet.has(option.value)}
					isAvailable={availableSet?.has(option.value) ?? true}
					onToggle={() => toggleFilter(group.key, option.value)}
				/>
			))}
		</div>
	);
};

const FilterPill = ({
	option,
	isSelectedFromUrl,
	isAvailable,
	onToggle,
}: {
	option: PLPFilterOption;
	isSelectedFromUrl: boolean;
	isAvailable: boolean;
	onToggle: () => void;
}) => {
	// Source the optimistic value from the URL state (updates synchronously
	// when `toggleFilter` writes the query param) rather than `option.selected`
	// which lags behind the server refetch. Without this, the pill briefly
	// flickers back to its old state between click and refetch landing.
	const [isSelected, setOptimistic] = useOptimistic(isSelectedFromUrl);
	// Selected pills stay clickable even when unavailable so the user can
	// always back out of a zero-result combination.
	const isDisabled = !isAvailable && !isSelected;
	return (
		<button
			type="button"
			aria-pressed={isSelected}
			disabled={isDisabled}
			onClick={() =>
				startTransition(() => {
					setOptimistic(!isSelected);
					onToggle();
				})
			}
			className={clsx(
				"inline-flex min-h-10 items-center gap-grid-component-spacing-2xs p-grid-component-spacing-2xs rounded-component-specific-button transition-colors",
				isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-fade-1",
			)}
		>
			{option.hex && (
				<span
					aria-hidden
					className={clsx(
						"inline-block size-3.5 shrink-0 rounded-full border border-black/10",
						isDisabled && "opacity-50",
					)}
					style={{ backgroundColor: option.hex }}
				/>
			)}
			<span
				className={clsx(
					"text-sm whitespace-nowrap",
					isDisabled ? "text-text-dark-text-3" : "text-text-dark-text-1",
					isSelected && "underline underline-offset-4 decoration-from-font",
				)}
			>
				{option.label}
			</span>
		</button>
	);
};

const FilterGroupSkeleton = () => {
	return (
		<div className="w-full border-b border-border-medium">
			<div className="flex w-full items-center gap-grid-component-spacing-4xs py-grid-component-spacing-sm">
				<Skeleton className="h-4 w-24 rounded-none" />
				<Add2 className="ml-auto size-3.5 shrink-0 text-text-sand-text-4 opacity-30" />
			</div>
		</div>
	);
};

const SUBGROUP_LABEL: Record<SizeCategory, string> = {
	adult: "Adult",
	children: "Children",
	other: "Other",
};

const SizeToggleGrid = ({
	group,
	selectedSet,
	availableSet,
	toggleFilter,
}: {
	group: PLPFilter;
	selectedSet: Set<string>;
	availableSet: Set<string> | undefined;
	toggleFilter: (key: string, value: string) => void;
}) => {
	const t = useTranslations();
	const subgroups = groupSizeOptions(group.options);
	const showHeadings = subgroups.length > 1;

	return (
		<div className="flex flex-col gap-grid-component-spacing-lg">
			{subgroups.map((sub, i) => (
				<div
					key={sub.category}
					className={clsx(
						"flex flex-col gap-grid-component-spacing-sm",
						// Hairline divider above every group after the first — visually
						// breaks adult/children into distinct sections without competing
						// with the accordion's outer border.
						showHeadings && i > 0 && "pt-grid-component-spacing-md border-t border-border-light",
					)}
				>
					{showHeadings && (
						<Typography variant="label-sm-reg" as="span" className="text-text-sand-text-4">
							{t(SUBGROUP_LABEL[sub.category])}
						</Typography>
					)}
					<div className="flex flex-wrap content-start items-start gap-grid-component-spacing-3xs">
						{sub.options.map((option) => (
							<FilterPill
								key={`${group.key}:${option.value}`}
								option={option}
								isSelectedFromUrl={selectedSet.has(option.value)}
								isAvailable={availableSet?.has(option.value) ?? true}
								onToggle={() => toggleFilter(group.key, option.value)}
							/>
						))}
					</div>
				</div>
			))}
		</div>
	);
};
